// EligibilityRequirements.js - Portugal
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, Briefcase, DollarSign, Languages, GraduationCap } from 'lucide-react';

const EligibilityRequirements = () => {
  const requirements = [
    {
      icon: DollarSign,
      title: 'Financial Requirements',
      description: 'Golden Visa: €500,000 investment minimum. D7 Visa: €820/month passive income (Portuguese minimum wage). Proof of funds and income source documentation required.',
      applicable: 'Golden Visa, D7 Visa'
    },
    {
      icon: Briefcase,
      title: 'Investment Documentation',
      description: 'For Golden Visa: proof of legal source of funds, investment contracts, property deeds, or business registration. All investments must be maintained for minimum 5 years.',
      applicable: 'Golden Visa'
    },
    {
      icon: Languages,
      title: 'Portuguese Language',
      description: 'Not required for initial residency. A2 level Portuguese required for citizenship application after 5 years. Language courses available in Portugal.',
      applicable: 'Citizenship (after 5 years)'
    },
    {
      icon: GraduationCap,
      title: 'Accommodation Proof',
      description: 'Proof of accommodation in Portugal (rental agreement, property ownership, or hotel booking). Required for all visa types. Can be arranged after visa approval.',
      applicable: 'All Programs'
    },
    {
      icon: CheckCircle2,
      title: 'Clean Criminal Record',
      description: 'Police clearance certificate from country of residence and any country lived in for 12+ months in past 5 years. Must have no serious criminal convictions.',
      applicable: 'All Programs'
    },
    {
      icon: FileText,
      title: 'Valid Travel Documents',
      description: 'Valid passport with minimum 3 months validity beyond intended stay. Travel insurance covering minimum €30,000 for medical expenses and repatriation.',
      applicable: 'All Programs'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            General Eligibility Requirements
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            While specific requirements vary by program, here are the common criteria for Portuguese residency applications.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requirements.map((requirement, index) => {
            const Icon = requirement.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#F5F8FA] p-6 rounded-lg border border-gray-200 hover:border-[#2542D4] transition-colors"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#202356]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#202356] mb-1">
                      {requirement.title}
                    </h3>
                    <span className="text-xs text-[#2542D4] font-light">
                      {requirement.applicable}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {requirement.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-lg p-8 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">
            Golden Visa Investment Options
          </h3>
          <p className="text-white/90 text-sm font-light leading-relaxed mb-4">
            Multiple investment routes available to qualify for Portuguese Golden Visa residency.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Capital Transfer</p>
              <p className="text-white/80 font-light">€500,000</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Investment Funds</p>
              <p className="text-white/80 font-light">€500,000</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Job Creation</p>
              <p className="text-white/80 font-light">10 jobs</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Note:</strong> Requirements and investment amounts are subject to change based on Portuguese immigration policy. We assess your profile against current requirements and recommend the best pathway for your situation.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EligibilityRequirements;
